import { useAuth } from "@clerk/clerk-react";
import { MenuItem, Select } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { BentoContact, NewBentoContact } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";

import AddNewContactDialog from "../../../../QuickSendIndividualDrawer/AddNewContact";
import styles from "./styles";

export type ContactSelection = {
  bentoContactId?: number;
  newContact?: NewBentoContact;
  email: string;
};

const ADD_KEY = "__add__";
const NEW_KEY = "__new__";

interface RecipientSelectProps {
  bentoBrandId: number;
  currentBentoContactId?: number;
  currentEmail?: string;
  selection: ContactSelection | null;
  onSelect: (selection: ContactSelection) => void;
  disabled?: boolean;
}

export default function RecipientSelect({
  bentoBrandId,
  currentBentoContactId,
  currentEmail,
  selection,
  onSelect,
  disabled,
}: RecipientSelectProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const [contacts, setContacts] = useState<BentoContact[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newContact, setNewContact] = useState<NewBentoContact>({
    email: "",
    name: "",
    title: "",
  });

  useEffect(() => {
    if (!currentOrg?.id || !bentoBrandId) {
      return;
    }
    const fetchContacts = async () => {
      try {
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/bento-brands/${bentoBrandId}/contacts`,
          "GET",
        );
        setContacts(res.selectableContacts ?? []);
      } catch (error) {
        setErrorAlert(error);
      }
    };
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, bentoBrandId]);

  const label = (contact: BentoContact | NewBentoContact) => {
    const firstName = contact.name?.split(" ")[0];
    const titleAndName = [firstName, contact.title].filter(Boolean).join(", ");
    return `${titleAndName ? `${titleAndName} ` : ""}(${contact.email})`;
  };

  const hasCurrent =
    currentBentoContactId &&
    contacts.some((contact) => contact.id === currentBentoContactId);

  const value = selection?.newContact
    ? NEW_KEY
    : selection?.bentoContactId
      ? String(selection.bentoContactId)
      : hasCurrent
        ? String(currentBentoContactId)
        : "";

  const handleChange = (key: string) => {
    if (key === ADD_KEY) {
      setAddOpen(true);
      return;
    }
    if (key === NEW_KEY) {
      return;
    }
    const contact = contacts.find((c) => String(c.id) === key);
    if (contact?.id) {
      onSelect({ bentoContactId: contact.id, email: contact.email });
    }
  };

  const handleConfirmNew = (contact: NewBentoContact) => {
    setAddOpen(false);
    onSelect({ newContact: contact, email: contact.email });
  };

  return (
    <>
      <Select
        size="small"
        fullWidth
        variant="standard"
        disableUnderline
        displayEmpty
        disabled={disabled}
        value={value}
        sx={styles.select}
        onChange={(e) => handleChange(e.target.value)}
        renderValue={(key) => {
          if (key === NEW_KEY && selection?.newContact) {
            return label(selection.newContact);
          }
          const contact = contacts.find((c) => String(c.id) === key);
          if (contact) {
            return label(contact);
          }
          return currentEmail || "Select a contact";
        }}
      >
        {contacts.map((contact) => (
          <MenuItem
            key={contact.id}
            value={String(contact.id)}
            sx={styles.menuItem}
          >
            {label(contact)}
          </MenuItem>
        ))}
        {selection?.newContact && (
          <MenuItem value={NEW_KEY} sx={styles.menuItem}>
            {label(selection.newContact)}
          </MenuItem>
        )}
        <MenuItem value={ADD_KEY} sx={styles.menuItem}>
          Add your own contact
        </MenuItem>
      </Select>

      <AddNewContactDialog
        open={addOpen}
        handleClose={() => setAddOpen(false)}
        handleConfirm={handleConfirmNew}
        contact={newContact}
        setContact={setNewContact}
      />
    </>
  );
}
